import { useEffect, useState, useCallback } from 'react';
import { supabase, withRetry } from '../lib/supabase';
import type { CompanySettings } from '../types';

export function useCompanySettings() {
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let result = await withRetry(async () => {
        return await supabase
          .from('company_settings')
          .select('*')
          .limit(1)
          .single();
      });

      if (result.error) {
        // If no settings exist, create default settings
        if (result.error.code === 'PGRST116') {
          const insertResult = await withRetry(async () => {
            return await supabase
              .from('company_settings')
              .insert({
                company_name: 'Your Company Name',
                company_address: 'Your Company Address',
                company_phone: 'Your Company Phone',
                display_logo: false
              })
              .select()
              .single();
          });

          if (insertResult.error) throw insertResult.error;
          result = insertResult;
        } else {
          throw result.error;
        }
      }

      if (result.data) {
        setSettings({
          id: result.data.id,
          companyName: result.data.company_name || '',
          companyAddress: result.data.company_address || '',
          companyPhone: result.data.company_phone || '',
          logoUrl: result.data.logo_url,
          displayLogo: result.data.display_logo,
          valmarUsername: result.data.valmar_username || '',
          valmarPassword: result.data.valmar_password || '',
          valmarEnvironment: result.data.valmar_environment || 'sandbox',
          valmarMerchantId: result.data.valmar_merchant_id || '',
          achEnabled: result.data.ach_enabled || false,
          taxFormEin: result.data.tax_form_ein || '',
          taxFormLegalName: result.data.tax_form_legal_name || '',
          taxFormTradeName: result.data.tax_form_trade_name || '',
          taxFormAddress: result.data.tax_form_address || ''
        });
      }
    } catch (err) {
      console.error('Error loading company settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSettings = async (newSettings: Partial<CompanySettings>) => {
    try {
      setError(null);

      const updateData: any = {
        id: settings?.id,
        company_name: newSettings.companyName,
        company_address: newSettings.companyAddress,
        company_phone: newSettings.companyPhone,
        logo_url: newSettings.logoUrl,
        display_logo: newSettings.displayLogo
      };

      if (newSettings.valmarUsername !== undefined) {
        updateData.valmar_username = newSettings.valmarUsername;
      }
      if (newSettings.valmarPassword !== undefined) {
        updateData.valmar_password = newSettings.valmarPassword;
      }
      if (newSettings.valmarEnvironment !== undefined) {
        updateData.valmar_environment = newSettings.valmarEnvironment;
      }
      if (newSettings.valmarMerchantId !== undefined) {
        updateData.valmar_merchant_id = newSettings.valmarMerchantId;
      }
      if (newSettings.achEnabled !== undefined) {
        updateData.ach_enabled = newSettings.achEnabled;
      }
      if (newSettings.taxFormEin !== undefined) {
        updateData.tax_form_ein = newSettings.taxFormEin;
      }
      if (newSettings.taxFormLegalName !== undefined) {
        updateData.tax_form_legal_name = newSettings.taxFormLegalName;
      }
      if (newSettings.taxFormTradeName !== undefined) {
        updateData.tax_form_trade_name = newSettings.taxFormTradeName;
      }
      if (newSettings.taxFormAddress !== undefined) {
        updateData.tax_form_address = newSettings.taxFormAddress;
      }

      const { error } = await withRetry(async () => {
        return await supabase
          .from('company_settings')
          .upsert(updateData);
      });

      if (error) throw error;
      await fetchSettings();
    } catch (err) {
      console.error('Error updating company settings:', err);
      throw err;
    }
  };

  const uploadLogo = async (file: File) => {
    try {
      setError(null);

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await withRetry(async () => {
        return await supabase.storage
          .from('company-logos')
          .upload(filePath, file);
      });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      await updateSettings({ logoUrl: publicUrl });
    } catch (err) {
      console.error('Error uploading logo:', err);
      throw err;
    }
  };

  return {
    settings,
    loading,
    error,
    updateSettings,
    uploadLogo,
    refetch: fetchSettings
  };
}